import { Button } from '@/components/form/Button';
import { Form } from '@/components/form/Form';
import { ButtonVariant } from '@/components/form/buttonHelper';
import { TextField } from '@/components/form/fields/text/TextInput';
import { Loading } from '@/components/loading/Loading';
import { Modal } from '@/components/modal/Modal';
import { ModalProps, useModal } from '@/components/modal/useModal';
import { Card } from '@/components/sections/Card';
import { useCurrentPurchaseOrderLink } from '@/lib/getMyLinks';
import { PoStatus, usePoName, useSavePurchaseOrder } from '@/services/purchaseOrders/usePurchaseOrderService';
import { zodResolver } from '@hookform/resolvers/zod';
import { format } from 'date-fns';
import { FC, FunctionComponent, Suspense, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { z } from 'zod';

export const SavePurchaseOrderFormFields = z.object({
  name: z.string().min(1, 'Required')
});

export type SavePurchaseOrderFormFields = z.infer<typeof SavePurchaseOrderFormFields>;

const SaveOrderForm: FC<{ onClose: () => void; overrideName?: string }> = ({ onClose, overrideName }) => {
  const defaultName = usePoName();
  const navigate = useNavigate();

  const methods = useForm<SavePurchaseOrderFormFields>({
    resolver: zodResolver(SavePurchaseOrderFormFields),
    defaultValues: {
      name: overrideName || defaultName || format(new Date(), 'dd/MM/yyyy HH:mm:SS')
    }
  });

  const saveOrder = useSavePurchaseOrder();

  const poLink = useCurrentPurchaseOrderLink();

  const [loading, setLoading] = useState(false);

  const onSubmit = async (data: SavePurchaseOrderFormFields) => {
    setLoading(true);
    await saveOrder(data.name, PoStatus.Draft);
    navigate(poLink);
  };

  return (
    <Form className="flex w-full flex-col space-y-3 py-3" methods={methods} onSubmit={onSubmit}>
      <p>Give your order a name, and you'll be able to come back to it at any time from the orders / quotes screen.</p>

      <TextField id="name" label="Name" />

      <div className="flex justify-between pt-2 space-x-2">
        <Button onClick={() => onClose()} loading={false} className="w-fit" variant={ButtonVariant.Mono} type="button">
          Cancel
        </Button>
        <Button loading={loading} className="w-fit" variant={ButtonVariant.Primary} type="submit">
          Confirm
        </Button>
      </div>
    </Form>
  );
};

interface Props extends ModalProps {
  overrideName?: string;
}

export const SavePurchaseOrderModal: FunctionComponent<Props> = props => {
  return (
    <Modal className="md:max-w-xl" {...props}>
      <Card className="w-full p-8">
        <Modal.Controls {...props}>
          <h1 className="text-2xl font-semibold">Save</h1>
        </Modal.Controls>
        <Suspense fallback={<Loading />}>
          <SaveOrderForm onClose={props.close} overrideName={props.overrideName} />
        </Suspense>
      </Card>
    </Modal>
  );
};

const SavePurchaseOrderButton: FC<{ variant?: ButtonVariant; customText?: string; overrideName?: string }> = ({
  variant = ButtonVariant.Secondary,
  customText,
  overrideName
}) => {
  const modalProps = useModal();
  return (
    <>
      <SavePurchaseOrderModal {...modalProps} overrideName={overrideName} />
      <Button
        variant={variant}
        onClick={() => {
          modalProps.open();
        }}
      >
        {customText || 'Save'}
      </Button>
    </>
  );
};

export default SavePurchaseOrderButton;
